import { AuthError, BrowserAuthErrorCodes } from '@azure/msal-browser';

/**
 * Errors that mean "the temporary cache holds a relic of an interrupted auth
 * attempt", not "authentication failed".
 *
 * handleRedirectPromise() throws these when it finds a half-written request
 * entry — left by a popup the user closed, a reload mid-redirect, or a version
 * upgrade — even though the user may hold a perfectly good session. MSAL
 * consumes the offending entry on read, so they also clear themselves.
 */
const STALE_AUTH_CACHE_CODES: readonly string[] = [
  BrowserAuthErrorCodes.noTokenRequestCacheError,
  BrowserAuthErrorCodes.unableToParseTokenRequestCacheError,
  BrowserAuthErrorCodes.noStateInHash,
  BrowserAuthErrorCodes.unableToParseState,
];

/**
 * Whether this error is a stale-cache relic that is safe to log and continue
 * past. Anything else — consent_required, invalid_grant, an expired or
 * replayed code, a server error — is a real authentication failure and must
 * not be swallowed: the user would be told nothing while the app either
 * admits them on a stale cached account or silently skips the fresh-login
 * path.
 */
export function isStaleAuthCacheError(error: unknown): boolean {
  return (
    error instanceof AuthError && STALE_AUTH_CACHE_CODES.includes(error.errorCode)
  );
}
